import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Button, Alert, Form, InputGroup } from 'react-bootstrap';
import { CenteredLoader } from '../../../components/LoadingSpinner';
import { api } from '../../../shared/api';
import { getContrastColor, stringToColor } from '../../../shared/colorUtils';
import { useSortPreference } from '../../../shared/useSortPreference';
import Select from '../../../components/form/AppSelect';
import DateRangeField from '../../../components/form/DateRangeField';
import '../../../shared/ReactSelectDarkMode.css';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../../shared/CurrencyContext';
import { usePermissions } from '../../../shared/permissions';
import { useConfirm } from '../../../shared/ConfirmContext';
import { useRealtimeEvents } from '../../../shared/useRealtimeEvents';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import PageSection from '../../../components/layout/PageSection';
import { useConflictCheck } from '../../../shared/useConflictCheck';

// The km field doubles as a day count for any non-"km" rate type (e.g. "pausal" —
// a per-day rate), so the total is always rate × km regardless of rate type.
const isDayBasedRateType = (rateType) => !!rateType && rateType !== 'km';

const selectTheme = theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: 'var(--tblr-primary)',
    primary75: 'var(--tblr-primary-rgb)',
    primary50: 'var(--tblr-primary-rgb)',
    primary25: 'var(--tblr-primary-rgb)',
    danger: 'var(--tblr-danger)',
    dangerLight: 'var(--tblr-danger-rgb)',
    neutral0: 'var(--tblr-bg-forms)',
    neutral5: 'var(--tblr-bg-surface)',
    neutral10: 'var(--tblr-bg-surface)',
    neutral20: 'var(--tblr-border-color)',
    neutral30: 'var(--tblr-border-color)',
    neutral40: 'var(--tblr-body-color-secondary)',
    neutral50: 'var(--tblr-body-color-secondary)',
    neutral60: 'var(--tblr-body-color-secondary)',
    neutral70: 'var(--tblr-body-color-secondary)',
    neutral80: 'var(--tblr-body-color)',
    neutral90: 'var(--tblr-body-color)',
  }
});

const ProjectVehicles = ({ projectId, onDirtyChange, projectStartDate, projectEndDate }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { canManage } = usePermissions();
  const { formatCurrency } = useCurrency();

  const queryClient = useQueryClient();
  const [vehicles, setVehicles] = useState([]);
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [tariffTypes, setTariffTypes] = useState([]);
  const [tariffTypesLoading, setTariffTypesLoading] = useState(true);
  const [vehicleTariffs, setVehicleTariffs] = useState([]);
  const [tariffOptions, setTariffOptions] = useState([]);
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [error, setError] = useState(null);
  const [, setTariffsLoading] = useState(false);

  const { isLoading: loading, isLoading: availableLoading, data: queryData } = useQuery({
    queryKey: ['project-vehicles', projectId],
    queryFn: async () => {
      setTariffTypesLoading(true);
      try {
        const [vehiclesRes, availableRes, tariffTypesRes] = await Promise.all([
          api.get(`projects/api/project-vehicles/?project_id=${projectId}`),
          api.get('transport/api/vehicles/'),
          api.get('transport/api/vehicle-tariff-types/?is_active=true'),
        ]);
        const normalizedTariffTypes = Array.isArray(tariffTypesRes) ? tariffTypesRes : (tariffTypesRes.results || []);
        setVehicles(vehiclesRes);
        setAvailableVehicles(availableRes);
        setTariffTypes(normalizedTariffTypes);
        return { vehicles: vehiclesRes, available: availableRes, tariffTypes: normalizedTariffTypes };
      } finally {
        setTariffTypesLoading(false);
      }
    },
  });

  useEffect(() => {
    if (!queryData) return;
    setVehicles(queryData.vehicles || []);
    setAvailableVehicles(queryData.available || []);
    setTariffTypes(queryData.tariffTypes || []);
    setTariffTypesLoading(false);
  }, [queryData]);

  const fetchVehicles = useCallback((silent = false) => {
    queryClient.invalidateQueries({ queryKey: ['project-vehicles', projectId] });
  }, [queryClient, projectId]);

  const [editingVehicleId, setEditingVehicleId] = useState(null);
  const [rowFormData, setRowFormData] = useState({ km: 0, rate_type: 'km', rate: 0, date_from: '', date_to: '' });
  const [pendingChanges, setPendingChanges] = useState(new Map());
  const hasUnsavedChanges = pendingChanges.size > 0;
  // Keep dirty ref in sync so the WS handler can check without a stale closure
  const isDirtyRef = useRef(false);
  useEffect(() => { isDirtyRef.current = hasUnsavedChanges; }, [hasUnsavedChanges]);

  // Report dirty state to parent (ProjectDetail handles the blocker)
  useEffect(() => { onDirtyChange?.(hasUnsavedChanges); }, [hasUnsavedChanges]);

  // ── realtime updates ─────────────────────────────────────────────────────
  const _vehiclesDebounce = useRef(null);

  useRealtimeEvents(useCallback((e) => {
    if (e.model !== 'projects.projectvehicle' && e.model !== 'transport.vehicle') return;
    if (isDirtyRef.current) return; // user has unsaved edits — skip
    clearTimeout(_vehiclesDebounce.current);
    _vehiclesDebounce.current = setTimeout(() => fetchVehicles(true), 400);
  }, [fetchVehicles])); // eslint-disable-line react-hooks/exhaustive-deps

  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useSortPreference('project-vehicles', { key: 'name', direction: 'asc' });
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };
  const rateTypeOptions = [
    { value: 'all', label: t('projects.vehicles.allRateTypes') },
    { value: 'km', label: t('projects.vehicles.perKm') },
    { value: 'flat', label: t('projects.vehicles.flatRate') },
  ];
  const [selectedRateType, setSelectedRateType] = useState({ value: 'all' });
  const selectedRateTypeOption = rateTypeOptions.find(o => o.value === selectedRateType.value) || rateTypeOptions[0];

  // tariff types fetched in main useQuery above

  const vehicleRows = useMemo(() => {
    return availableVehicles
      .map(vehicle => ({
        ...vehicle,
        projectVehicle: vehicles.find(v => v.vehicle === vehicle.id) || null,
      }))
      .filter(vehicle => {
        if (searchQuery && !vehicle.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
        if (selectedRateType?.value !== 'all' && vehicle.projectVehicle) {
          if (vehicle.projectVehicle.rate_type !== selectedRateType.value) return false;
        }
        return true;
      });
  }, [availableVehicles, vehicles, searchQuery, selectedRateType]);

  const sortedRows = useMemo(() => {
    if (!sortConfig.key) return vehicleRows;
    return [...vehicleRows].sort((a, b) => {
      const aPV = pendingChanges.get(a.id) || a.projectVehicle;
      const bPV = pendingChanges.get(b.id) || b.projectVehicle;
      let aVal, bVal;
      switch (sortConfig.key) {
        case 'name':      aVal = a.name || ''; bVal = b.name || ''; break;
        case 'category':  aVal = a.category_name || ''; bVal = b.category_name || ''; break;
        case 'rate_type': aVal = aPV?.rate_type || ''; bVal = bPV?.rate_type || ''; break;
        case 'rate':      aVal = parseFloat(aPV?.rate || 0); bVal = parseFloat(bPV?.rate || 0); break;
        case 'km':        aVal = parseFloat(aPV?.km || 0); bVal = parseFloat(bPV?.km || 0); break;
        case 'total':
          aVal = aPV ? parseFloat(aPV.rate || 0) * parseFloat(aPV.km || 0) : 0;
          bVal = bPV ? parseFloat(bPV.rate || 0) * parseFloat(bPV.km || 0) : 0;
          break;
        default: aVal = ''; bVal = '';
      }
      if (typeof aVal === 'string') {
        const cmp = aVal.localeCompare(bVal);
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      }
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [vehicleRows, sortConfig, pendingChanges]);

  const conflictResult = useConflictCheck('vehicle', {
    vehicle: rowFormData.vehicle,
    date_from: rowFormData.date_from || projectStartDate,
    date_to: rowFormData.date_to || projectEndDate,
    exclude_vehicle_assignment_id: vehicles.find(v => v.vehicle === editingVehicleId)?.id,
  }, !!editingVehicleId);

  const fetchVehicleTariffs = async (vehicleId) => {
    if (!vehicleId) {
      setVehicleTariffs([]);
      setTariffOptions([]);
      setSelectedTariff(null);
      return;
    }
    try {
      setTariffsLoading(true);
      const response = await api.get(`transport/api/vehicle-tariffs/?vehicle=${vehicleId}&is_active=true`);
      const tariffs = Array.isArray(response) ? response : (response.results || []);
      setVehicleTariffs(tariffs);
      const opts = tariffs.map(tariff => ({
        value: tariff.id,
        label: `${tariff.name} — ${tariff.tariff_type_detail?.name || ''} · ${tariff.amount} ${tariff.tariff_type_detail?.unit || ''}`,
        tariff,
      }));
      setTariffOptions(opts);
      setSelectedTariff(null);
      return tariffs;
    } catch (err) {
      console.error('Error fetching vehicle tariffs:', err);
      setVehicleTariffs([]);
      setTariffOptions([]);
      return [];
    } finally {
      setTariffsLoading(false);
    }
  };

  const handleTariffSelect = (selectedOption) => {
    setSelectedTariff(selectedOption);
    if (!selectedOption) return;
    const tariff = selectedOption.tariff;
    const code = tariff.tariff_type_detail?.code || 'km';
    setRowFormData(prev => ({ ...prev, rate: tariff.amount, rate_type: code }));
  };

  const handleStartAdd = (vehicle) => {
    setEditingVehicleId(vehicle.id);
    const initialRateType = tariffTypes[0]?.code || 'km';
    setRowFormData({ project: projectId, vehicle: vehicle.id, km: 0, rate_type: initialRateType, rate: 0, date_from: projectStartDate || '', date_to: projectEndDate || '' });
    fetchVehicleTariffs(vehicle.id).then(tariffs => {
      const match = tariffs.find(t => t.tariff_type_detail?.code === initialRateType);
      if (match) setRowFormData(prev => ({ ...prev, rate: match.amount }));
    });
  };

  const handleStartEdit = (vehicle) => {
    const pending = pendingChanges.get(vehicle.id);
    const source = (pending && !pending.isDeleted) ? pending : vehicle.projectVehicle;
    const currentRateType = source?.rate_type || tariffTypes[0]?.code || 'km';
    setEditingVehicleId(vehicle.id);
    setRowFormData({
      project: projectId,
      vehicle: vehicle.id,
      km: source?.km || 0,
      rate_type: currentRateType,
      rate: source?.rate || 0,
      date_from: source?.date_from || '',
      date_to: source?.date_to || '',
    });
    fetchVehicleTariffs(vehicle.id).then(tariffs => {
      const match = tariffs.find(t => t.tariff_type_detail?.code === currentRateType);
      if (match) setRowFormData(prev => ({ ...prev, rate: match.amount }));
    });
  };

  const commitRowToPending = (formData, vehicleId) => {
    if (!vehicleId) return;
    const existingPV = vehicles.find(v => v.vehicle === vehicleId);
    setPendingChanges(prev => {
      const next = new Map(prev);
      next.set(vehicleId, {
        ...formData,
        isNew: !existingPV,
        isDeleted: false,
        pvId: existingPV?.id,
      });
      return next;
    });
  };

  const exitEdit = () => {
    setEditingVehicleId(null);
    setRowFormData({ km: 0, rate_type: 'km', rate: 0, date_from: '', date_to: '' });
    setVehicleTariffs([]);
    setTariffOptions([]);
    setSelectedTariff(null);
  };

  const handleRowFormChange = (e) => {
    const { name, value } = e.target;
    setRowFormData(prev => {
      const next = { ...prev, [name]: value };
      commitRowToPending(next, editingVehicleId);
      return next;
    });
  };

  const handleSaveAll = async () => {
    try {
      // Fetch fresh server state first to avoid last-write-wins overwrite
      const freshVehicles = await api.get(`projects/api/project-vehicles/?project_id=${projectId}`);

      const promises = [];
      pendingChanges.forEach((change, vehicleId) => {
        // Re-derive server record from fresh data
        const serverRecord = freshVehicles.find(v => v.vehicle === vehicleId);
        if (change.isDeleted) {
          const pvId = serverRecord?.id ?? change.pvId;
          if (pvId) promises.push(api.delete(`projects/api/project-vehicles/${pvId}/`));
        } else if (!serverRecord) {
          promises.push(api.post('projects/api/project-vehicles/', {
            project: projectId, vehicle: vehicleId,
            km: change.km, rate_type: change.rate_type, rate: change.rate,
            date_from: change.date_from || null, date_to: change.date_to || null,
          }));
        } else {
          promises.push(api.patch(`projects/api/project-vehicles/${serverRecord.id}/`, {
            km: change.km, rate_type: change.rate_type, rate: change.rate,
            date_from: change.date_from || null, date_to: change.date_to || null,
          }));
        }
      });
      await Promise.all(promises);
      setPendingChanges(new Map());
      fetchVehicles();
    } catch (err) {
      console.error('Error saving all changes:', err);
      setError(t('projects.vehicles.errorSaving'));
    }
  };

  const handleCancelAll = () => {
    setPendingChanges(new Map());
    exitEdit();
  };

  const handleDelete = async (pvId, vehicleId) => {
    if (await confirm(t('projects.vehicles.confirmDelete'))) {
      setPendingChanges(prev => {
        const next = new Map(prev);
        if (!pvId) {
          next.delete(vehicleId);
        } else {
          next.set(vehicleId, { isDeleted: true, pvId });
        }
        return next;
      });
    }
  };

  if (loading || availableLoading) return <CenteredLoader />;

  const sortIcon = (key) => (
    sortConfig.key === key
      ? <i className={`ti ti-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'} ms-1`}></i>
      : <i className="ti ti-selector ms-1 opacity-25"></i>
  );

  return (
    <PageSection
      title={t('projects.vehicles.title')}
      description={t('projects.vehicles.description')}
      bodyClassName="p-0"
    >
      <div className="px-3 pt-3">
        <div className="mb-3 row">
          <div className="col-md-8 mb-2 mb-md-0">
            <InputGroup>
              <Form.Control
                placeholder={t('projects.vehicles.searchPlaceholder')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </InputGroup>
          </div>
          <div className="col-md-4">
            <Select
              value={selectedRateTypeOption}
              onChange={setSelectedRateType}
              options={rateTypeOptions}
              placeholder={t('projects.vehicles.filterByRateType')}
              theme={selectTheme}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
        </div>

        {error && <Alert variant="danger" className="app-inline-alert">{error}</Alert>}

        {hasUnsavedChanges && (
          <Alert variant="warning" className="app-inline-alert mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong>{pendingChanges.size}</strong> {t('projects.vehicles.unsavedChanges')}
              </div>
              <div className="d-flex gap-2">
                <Button variant="outline-secondary" size="sm" onClick={handleCancelAll}>
                  {t('common.cancel')}
                </Button>
                <Button variant="warning" size="sm" onClick={handleSaveAll} className="fw-medium">
                  <i className="ti ti-check me-1"></i>
                  {t('common.save')} ({pendingChanges.size})
                </Button>
              </div>
            </div>
          </Alert>
        )}

        {sortedRows.length === 0 ? <Alert variant="info" className="app-inline-alert mb-3">{t('projects.vehicles.noVehicles')}</Alert> : null}
      </div>

      {sortedRows.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-hover table-vcenter mb-0">
              <thead>
                <tr>
                  <th style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort('name')}>
                    {t('projects.vehicles.vehicle')}{sortIcon('name')}
                  </th>
                  <th style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort('category')}>
                    {t('projects.vehicles.category')}{sortIcon('category')}
                  </th>
                  <th style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort('rate_type')}>
                    {t('projects.vehicles.rateType')}{sortIcon('rate_type')}
                  </th>
                  <th className="text-end" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort('rate')}>
                    {t('projects.vehicles.rate')}{sortIcon('rate')}
                  </th>
                  <th className="text-end" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort('km')}>
                    {t('projects.vehicles.kmOrDays')}{sortIcon('km')}
                  </th>
                  <th className="d-none d-xl-table-cell" style={{ whiteSpace: 'nowrap' }}>
                    {t('projects.vehicles.dateRange', 'Termín')}
                  </th>
                  <th className="text-end" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => handleSort('total')}>
                    {t('projects.vehicles.total')}{sortIcon('total')}
                  </th>
                  <th className="text-center" title={t('projects.vehicles.hideLicencePlate', 'Skrýt SPZ')}><i className="ti ti-eye-off"></i></th>
                  <th className="text-center">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map(vehicle => {
                  const pendingEntry = pendingChanges.get(vehicle.id);
                  const hasPending = !!pendingEntry && !pendingEntry?.isDeleted;
                  const isMarkedForDeletion = pendingEntry?.isDeleted === true;
                  const isEditing = editingVehicleId === vehicle.id;
                  const inProject = (!!vehicle.projectVehicle && !isMarkedForDeletion) || hasPending;
                  const pv = hasPending ? pendingEntry : vehicle.projectVehicle;

                  return (
                    <React.Fragment key={vehicle.id}>
                      <tr
                        className={`${isEditing ? 'table-warning' : inProject ? 'table-success' : ''} ${hasPending && !isEditing ? 'border-warning border-2' : ''} ${isMarkedForDeletion ? 'opacity-50' : ''}`}
                        onClick={(e) => {
                          if (e.target.closest('button') || e.target.closest('input') || e.target.closest('select') || e.target.closest('.react-select__control')) return;
                          if (isMarkedForDeletion || !canManage) return;
                          if (isEditing) { exitEdit(); return; }
                          if (editingVehicleId) exitEdit();
                          if (inProject) {
                            handleStartEdit(vehicle);
                          } else {
                            handleStartAdd(vehicle);
                          }
                        }}
                        style={{ cursor: !isEditing && !isMarkedForDeletion && canManage ? 'pointer' : 'default' }}
                      >
                        <td>
                          <div className="fw-medium">{vehicle.name}</div>
                          {vehicle.license_plate && <small className="text-muted">{vehicle.license_plate}</small>}
                        </td>
                        <td>
                          {vehicle.category_name && (
                            <span className="badge" style={{
                              backgroundColor: vehicle.category_color || 'var(--tblr-secondary)',
                              color: vehicle.category_color ? getContrastColor(vehicle.category_color) : '#fff'
                            }}>
                              {vehicle.category_name}
                            </span>
                          )}
                        </td>
                        {isEditing ? (
                          <>
                            <td style={{ minWidth: 180 }}>
                              <Select
                                value={tariffTypes.length > 0 ? { value: rowFormData.rate_type, label: tariffTypes.find(tt => tt.code === rowFormData.rate_type)?.name || rowFormData.rate_type } : null}
                                onChange={opt => {
                                  const code = opt ? opt.value : (tariffTypes[0]?.code || 'km');
                                  const match = vehicleTariffs.find(t => t.tariff_type_detail?.code === code);
                                  setRowFormData(prev => {
                                    const next = { ...prev, rate_type: code, ...(match ? { rate: match.amount } : {}) };
                                    commitRowToPending(next, editingVehicleId);
                                    return next;
                                  });
                                }}
                                options={tariffTypes.map(tt => ({ value: tt.code, label: tt.name }))}
                                isLoading={tariffTypesLoading}
                                placeholder={t('projects.vehicles.selectTariff')}
                                theme={selectTheme}
                                className="react-select-container"
                                classNamePrefix="react-select"
                              />
                            </td>
                            <td>
                              <Form.Control size="sm" type="number" name="rate" value={rowFormData.rate} onChange={handleRowFormChange} step="any" min="0" />
                            </td>
                            <td>
                              <InputGroup size="sm">
                                <Form.Control type="number" name="km" value={rowFormData.km} onChange={handleRowFormChange} min="0" autoFocus onFocus={(e) => e.target.select()} />
                                <InputGroup.Text>
                                  {isDayBasedRateType(rowFormData.rate_type) ? t('projects.vehicles.days') : t('projects.vehicles.km')}
                                </InputGroup.Text>
                              </InputGroup>
                            </td>
                            <td className="d-none d-xl-table-cell">
                              <DateRangeField
                                dateFrom={rowFormData.date_from}
                                dateTo={rowFormData.date_to}
                                projectStartDate={projectStartDate}
                                projectEndDate={projectEndDate}
                                onChange={({ dateFrom, dateTo }) => setRowFormData(prev => {
                                  const next = { ...prev, date_from: dateFrom || '', date_to: dateTo || '' };
                                  commitRowToPending(next, editingVehicleId);
                                  return next;
                                })}
                                warning={conflictResult?.count > 0 && (
                                  <div className="text-warning small">
                                    <small>⚠ {t('projects.vehicles.conflictWarning', { count: conflictResult.count, defaultValue: 'Konflikt s {{count}} jiným projektem' })}</small>
                                  </div>
                                )}
                              />
                            </td>
                            <td className="text-end fw-semibold" style={{ color: 'var(--tblr-primary)' }}>
                              {formatCurrency(parseFloat(rowFormData.rate || 0) * parseFloat(rowFormData.km || 0))}
                            </td>
                            <td className="text-center">
                              {inProject && (
                                <Form.Check
                                  type="checkbox"
                                  checked={!!(hasPending ? pendingEntry?.hide_licence_plate : vehicle.projectVehicle?.hide_licence_plate)}
                                  onChange={async (e) => {
                                    const val = e.target.checked;
                                    const pvId = vehicle.projectVehicle?.id || pendingEntry?.pvId;
                                    if (!pvId) return;
                                    try {
                                      await api.patch(`projects/api/project-vehicles/${pvId}/`, { hide_licence_plate: val });
                                      fetchVehicles();
                                    } catch (err) { console.error(err); }
                                  }}
                                  title={t('projects.vehicles.hideLicencePlate', 'Skrýt SPZ')}
                                />
                              )}
                            </td>
                            <td className="text-center">
                              <Button size="sm" variant="outline-secondary" onClick={exitEdit} title={t('common.cancel')}>
                                <i className="ti ti-x"></i>
                              </Button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td>
                              {inProject && (() => {
                                const tt = tariffTypes.find(tt => tt.code === pv.rate_type);
                                const label = tt?.name || pv.rate_type;
                                const bg = tt?.color || stringToColor(label);
                                return (
                                  <span className="badge" style={{ backgroundColor: bg, color: getContrastColor(bg), fontWeight: 500 }}>
                                    {label}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="text-end">
                              {inProject
                                ? <>{formatCurrency(pv.rate)}{pv.rate_type === 'km' && <small className="text-muted ms-1">/km</small>}{isDayBasedRateType(pv.rate_type) && <small className="text-muted ms-1">/{t('projects.vehicles.day')}</small>}</>
                                : <span className="text-muted">-</span>}
                            </td>
                            <td className="text-end">
                              {inProject
                                ? <>{pv.km}{isDayBasedRateType(pv.rate_type) && <small className="text-muted ms-1">{t('projects.vehicles.days')}</small>}</>
                                : <span className="text-muted">-</span>}
                            </td>
                            <td className="d-none d-xl-table-cell">
                              {inProject ? (
                                (pv.date_from || pv.date_to)
                                  ? <small>{pv.date_from || '…'} – {pv.date_to || '…'}</small>
                                  : <small className="text-muted">{t('projects.vehicles.wholeProject', 'Celý projekt')}</small>
                              ) : <span className="text-muted">-</span>}
                            </td>
                            <td className="text-end fw-semibold" style={{ color: inProject ? 'var(--tblr-primary)' : undefined }}>
                              {inProject
                                ? formatCurrency(pv.rate * pv.km)
                                : <span className="text-muted">-</span>}
                            </td>
                            <td className="text-center">
                              {inProject && (
                                <Form.Check
                                  type="checkbox"
                                  checked={!!pv?.hide_licence_plate}
                                  onChange={async (e) => {
                                    const val = e.target.checked;
                                    const pvId = vehicle.projectVehicle?.id || pv?.pvId;
                                    if (!pvId) return;
                                    try {
                                      await api.patch(`projects/api/project-vehicles/${pvId}/`, { hide_licence_plate: val });
                                      fetchVehicles();
                                    } catch (err) { console.error(err); }
                                  }}
                                  title={t('projects.vehicles.hideLicencePlate', 'Skrýt SPZ')}
                                />
                              )}
                            </td>
                            <td className="text-center">
                              {canManage && (
                                inProject ? (
                                  <div className="btn-group">
                                    <Button size="sm" variant="outline-info" onClick={() => handleStartEdit(vehicle)} title={t('common.edit')}>
                                      <i className="ti ti-edit"></i>
                                    </Button>
                                    <Button size="sm" variant="outline-danger" onClick={() => handleDelete(vehicle.projectVehicle?.id, vehicle.id)} title={t('projects.vehicles.remove')}>
                                      <i className="ti ti-trash"></i>
                                    </Button>
                                  </div>
                                ) : (
                                  <Button size="sm" variant="outline-success" onClick={() => handleStartAdd(vehicle)} title={t('projects.vehicles.add')}>
                                    <i className="ti ti-plus"></i>
                                  </Button>
                                )
                              )}
                            </td>
                          </>
                        )}
                      </tr>

                    </React.Fragment>
                  );
                })}
              </tbody>
              {vehicles.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan="6" className="text-end text-muted fw-medium">{t('projects.vehicles.total')}</td>
                    <td className="text-end fw-bold" style={{ color: 'var(--tblr-primary)' }}>
                      {formatCurrency(vehicles.reduce((sum, v) => sum + parseFloat(v.rate || 0) * parseFloat(v.km || 0), 0))}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
          </table>
        </div>
      ) : null}
    </PageSection>
  );
};

export default ProjectVehicles;
